import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';

/* ─── Shared primitives (same style as Terms/Privacy pages) ─── */

const SectionHeader = ({ num, tag, title, color = '#F97316' }) => (
  <div style={{ marginBottom: '36px', position: 'relative' }}>
    <div
      style={{
        fontFamily: 'Impact, sans-serif',
        fontSize: 'clamp(4rem, 10vw, 8rem)',
        fontWeight: 900,
        color: `${color}08`,
        lineHeight: 1,
        position: 'absolute',
        top: '-20px',
        left: '-10px',
        userSelect: 'none',
        letterSpacing: '-0.04em'
      }}
    >
      {num}
    </div>
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          color: color,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}
      >
        {tag}
      </div>
      <h2
        style={{
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(1.5rem, 3.5vw, 2.75rem)',
          fontWeight: 900,
          color: '#EEF0F2',
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          lineHeight: 1
        }}
      >
        {title}
      </h2>
    </div>
  </div>
);

const BodyText = ({ children }) => (
  <p
    style={{
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      color: 'rgba(238,240,242,0.72)',
      lineHeight: 1.85,
      marginBottom: '20px',
      maxWidth: '760px'
    }}
  >
    {children}
  </p>
);

const PolaroidCallout = ({ title, children, accent = '#F97316' }) => (
  <div
    style={{
      background: `${accent}08`,
      border: `1px solid ${accent}25`,
      borderLeft: `4px solid ${accent}`,
      padding: '24px 28px',
      marginBottom: '24px',
      position: 'relative',
      transform: 'rotate(-0.3deg)'
    }}
  >
    <div
      style={{
        fontFamily: 'Syne, sans-serif',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: accent,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: '10px'
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontFamily: 'Georgia, serif',
        fontSize: '0.9375rem',
        color: 'rgba(238,240,242,0.75)',
        lineHeight: 1.75
      }}
    >
      {children}
    </div>
  </div>
);

const ColorBlock = ({ children, color = '#3B6FD4' }) => (
  <div
    style={{
      background: `${color}0A`,
      border: `1px solid ${color}20`,
      borderTop: `3px solid ${color}`,
      padding: '20px 24px',
      marginBottom: '20px'
    }}
  >
    {children}
  </div>
);

const Divider = ({ color = '#F97316' }) => (
  <div style={{ margin: '48px 0', display: 'flex', alignItems: 'center', gap: '16px' }}>
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
    <div style={{ width: '8px', height: '8px', background: color, transform: 'rotate(45deg)' }} />
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
  </div>
);

/* ─── Table of Contents ─── */
const TableOfContents = () => {
  const items = [
    { id: 'samtykke', label: 'Samtykke og påmelding' },
    { id: 'meldingstyper', label: 'Typer SMS-meldinger' },
    { id: 'frekvens', label: 'Meldingsfrekvens' },
    { id: 'avmelding', label: 'Avmelding (STOPP)' },
    { id: 'kostnader', label: 'Priser og operatørkostnader' },
    { id: 'personvern', label: 'Personvern og telefonnummer' },
    { id: 'kontakt', label: 'Kontaktinformasjon' },
  ];

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        padding: '24px'
      }}
    >
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.625rem',
          color: '#F97316',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '16px'
        }}
      >
        Innhold
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items?.map((item, i) => (
          <a
            key={i}
            href={`#${item?.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              padding: '6px 0',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              transition: 'all 150ms ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.paddingLeft = '6px'; }}
            onMouseLeave={e => { e.currentTarget.style.paddingLeft = '0'; }}
          >
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.5625rem',
                color: '#F97316',
                minWidth: '20px'
              }}
            >
              {String(i + 1)?.padStart(2, '0')}
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8125rem',
                color: 'rgba(238,240,242,0.65)',
                lineHeight: 1.4
              }}
            >
              {item?.label}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};

/* ─── Hero ─── */
const SMSHero = () => (
  <div
    style={{
      paddingTop: '120px',
      paddingBottom: '60px',
      paddingLeft: '5%',
      paddingRight: '5%',
      background: 'linear-gradient(180deg, rgba(249,115,22,0.04) 0%, transparent 100%)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      position: 'relative',
      overflow: 'hidden'
    }}
  >
    {/* Ghost background text */}
    <div
      style={{
        position: 'absolute',
        top: '40px',
        right: '5%',
        fontFamily: 'Impact, sans-serif',
        fontSize: 'clamp(5rem, 15vw, 14rem)',
        fontWeight: 900,
        color: 'rgba(249,115,22,0.04)',
        lineHeight: 1,
        userSelect: 'none',
        letterSpacing: '-0.04em',
        pointerEvents: 'none'
      }}
    >
      SMS
    </div>

    <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          color: '#F97316',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          marginBottom: '16px'
        }}
      >
        Juridisk dokument · NordPass AS · v1.0
      </div>

      <h1
        style={{
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(2.5rem, 7vw, 6rem)',
          fontWeight: 900,
          color: '#EEF0F2',
          textTransform: 'uppercase',
          letterSpacing: '-0.03em',
          lineHeight: 0.95,
          marginBottom: '24px'
        }}
      >
        SMS OPT-IN<br />
        <span style={{ color: '#F97316' }}>VILKÅR</span>
      </h1>

      <p
        style={{
          fontFamily: 'Lora, Georgia, serif',
          fontStyle: 'italic',
          fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
          color: 'rgba(238,240,242,0.55)',
          maxWidth: '600px',
          lineHeight: 1.65,
          marginBottom: '32px'
        }}
      >
        Vilkår og betingelser for SMS-varsler og markedsføring fra NordPass AS. Les nøye før du samtykker til å motta tekstmeldinger.
      </p>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'Sist oppdatert', value: 'Mars 2026' },
          { label: 'Versjon', value: '1.0' },
          { label: 'Jurisdiksjon', value: 'Norge' },
        ]?.map((item, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.5625rem',
                color: 'rgba(138,149,163,0.5)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}
            >
              {item?.label}
            </span>
            <span
              style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#EEF0F2'
              }}
            >
              {item?.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── Main Page ─── */
const SMSOptinVilkarPage = () => {
  return (
    <>
      <Helmet>
        <title>SMS Opt-in Vilkår — NordPass AS</title>
        <meta
          name="description"
          content="NordPass sine vilkår for SMS-varsler og markedsføring. Les om samtykke, meldingstyper, frekvens, avmelding og personvern for SMS-tjenesten."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Sticky nav */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.92)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '0 5%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '72px'
            }}
          >
            <Link
              to="/nord-pass-landing-page"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#3B6FD4',
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}
              />
              <span
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '1.125rem',
                  fontWeight: 800,
                  color: '#EEF0F2',
                  letterSpacing: '-0.03em'
                }}
              >
                NordPass
              </span>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {[
                { label: 'Hjem', href: '/nord-pass-landing-page' },
                { label: 'Tjenester', href: '/tjenester-services' },
                { label: 'Om Oss', href: '/om-oss-about-us' },
                { label: 'Kontakt', href: '/nord-pass-contact-page' },
              ]?.map((link, i) => (
                <Link
                  key={i}
                  to={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            <Link
              to="/login"
              style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: '#EEF0F2',
                textDecoration: 'none',
                padding: '10px 20px',
                background: '#3B6FD4',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}
            >
              Logg inn
            </Link>
          </div>
        </nav>

        {/* Breadcrumb */}
        <Breadcrumb
          crumbs={[
            { label: 'Hjem', href: '/nord-pass-landing-page' },
            { label: 'Juridisk', href: '/privacy-policy-personvern' },
            { label: 'SMS Opt-in Vilkår' }
          ]}
        />

        {/* Hero */}
        <SMSHero />

        {/* Main layout */}
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '80px 5%',
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: '60px',
            alignItems: 'start'
          }}
          className="sms-layout"
        >
          {/* Sticky sidebar */}
          <aside style={{ position: 'sticky', top: '100px' }}>
            <TableOfContents />

            {/* SMS badge */}
            <div
              style={{
                marginTop: '24px',
                background: 'rgba(249,115,22,0.06)',
                border: '1px solid rgba(249,115,22,0.2)',
                padding: '20px',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  fontFamily: 'Impact, sans-serif',
                  fontSize: '2rem',
                  color: '#F97316',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}
              >
                SMS
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.625rem',
                  color: 'rgba(249,115,22,0.7)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}
              >
                SEND STOPP FOR Å AVSLUTTE
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.625rem',
                  color: 'rgba(59,111,212,0.7)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}
              >
                SEND HJELP FOR STØTTE
              </div>
            </div>

            {/* Cross-links */}
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link
                to="/privacy-policy-personvern"
                style={{
                  display: 'block',
                  background: 'rgba(59,111,212,0.06)',
                  border: '1px solid rgba(59,111,212,0.2)',
                  padding: '14px 16px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 150ms ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.06)'; }}
              >
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.625rem', color: '#3B6FD4', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Se også</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2' }}>Personvernerklæring →</div>
              </Link>
              <Link
                to="/terms-conditions-vilk-r-betingelser"
                style={{
                  display: 'block',
                  background: 'rgba(249,115,22,0.06)',
                  border: '1px solid rgba(249,115,22,0.2)',
                  padding: '14px 16px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 150ms ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.06)'; }}
              >
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.625rem', color: '#F97316', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Se også</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2' }}>Vilkår & Betingelser →</div>
              </Link>
            </div>
          </aside>

          {/* Main content */}
          <main>

            {/* ── Compliance Summary Block (required by SMS marketing regulations) ── */}
            <div
              style={{
                background: 'rgba(59,111,212,0.06)',
                border: '2px solid rgba(59,111,212,0.25)',
                borderTop: '4px solid #3B6FD4',
                padding: '28px 32px',
                marginBottom: '64px'
              }}
            >
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.625rem',
                  color: '#3B6FD4',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  marginBottom: '16px'
                }}
              >
                SMS-program — Sammendrag av vilkår
              </div>
              <div
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '0.9375rem',
                  color: 'rgba(238,240,242,0.75)',
                  lineHeight: 1.85
                }}
              >
                <p style={{ margin: '0 0 12px 0' }}>
                  <strong style={{ color: '#EEF0F2', fontFamily: 'Syne, sans-serif' }}>Program:</strong>{' '}
                  NordPass AS SMS-varsler — tjenestevarsler, veiinformasjon, markedsføring og sikkerhetsmeldinger.
                </p>
                <p style={{ margin: '0 0 12px 0' }}>
                  <strong style={{ color: '#EEF0F2', fontFamily: 'Syne, sans-serif' }}>Meldingsfrekvens:</strong>{' '}
                  Varierer. Opptil 4 markedsføringsmeldinger per måned; tjeneste- og sikkerhetsvarsler sendes etter behov.
                </p>
                <p style={{ margin: '0 0 12px 0' }}>
                  <strong style={{ color: '#10B981', fontFamily: 'Syne, sans-serif' }}>Avmelding:</strong>{' '}
                  Svar <strong style={{ color: '#10B981' }}>STOPP</strong> for å avslutte. Du vil motta en bekreftelse og ikke flere meldinger.
                </p>
                <p style={{ margin: '0 0 12px 0' }}>
                  <strong style={{ color: '#3B6FD4', fontFamily: 'Syne, sans-serif' }}>Hjelp:</strong>{' '}
                  Svar <strong style={{ color: '#3B6FD4' }}>HJELP</strong> for kundestøtte, eller kontakt oss på{' '}
                  <a href="mailto:hei@nordpassno.com" style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}>hei@nordpassno.com</a>{' '}
                  / <a href="tel:+4720948903" style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}>+47 20 94 89 03</a>.
                </p>
                <p style={{ margin: '0 0 12px 0' }}>
                  <strong style={{ color: '#F97316', fontFamily: 'Syne, sans-serif' }}>Priser:</strong>{' '}
                  Meldingspriser og datapriser fra din mobiloperatør kan påløpe. Mobiloperatørene er ikke ansvarlige for forsinkede eller ikke-leverte meldinger.
                </p>
                <p style={{ margin: 0 }}>
                  <strong style={{ color: '#EEF0F2', fontFamily: 'Syne, sans-serif' }}>Personvern:</strong>{' '}
                  Les vår{' '}
                  <Link
                    to="/privacy-policy-personvern"
                    style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}
                  >
                    Personvernerklæring
                  </Link>{' '}
                  på nordpassno.com/privacy-policy-personvern.
                </p>
              </div>
            </div>

            {/* ── Section 1: Samtykke ── */}
            <section id="samtykke" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="01"
                tag="§ 1 — SAMTYKKE OG PÅMELDING"
                title="Samtykke og påmelding"
                color="#F97316"
              />

              <BodyText>
                Ved å oppgi ditt mobilnummer og krysse av for SMS-samtykke på NordPass sine nettsider, kontaktskjema eller abonnementsportal, gir du NordPass AS («NordPass», «vi», «oss») uttrykkelig tillatelse til å sende deg tekstmeldinger (SMS) til det oppgitte nummeret.
              </BodyText>

              <BodyText>
                Samtykket er frivillig. Du er ikke forpliktet til å samtykke til SMS-kommunikasjon som vilkår for å kjøpe produkter eller tjenester fra NordPass. Tjenestene er tilgjengelige uavhengig av om du velger å motta SMS-varsler.
              </BodyText>

              <PolaroidCallout title="Viktig — Frivillig samtykke" accent="#F97316">
                Samtykke til SMS er frivillig og kan trekkes tilbake når som helst uten kostnad. Avmelding påvirker ikke din tilgang til NordPass sine tjenester eller abonnement.
              </PolaroidCallout>

              <ColorBlock color="#3B6FD4">
                <p
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    fontSize: '0.9375rem',
                    color: 'rgba(238,240,242,0.6)',
                    lineHeight: 1.7,
                    margin: 0
                  }}
                >
                  <strong style={{ color: '#3B6FD4', fontStyle: 'normal' }}>Alder:</strong>{' '}
                  Du bekrefter at du er minst 18 år gammel, eller at du har samtykke fra en forelder eller verge, for å motta SMS-kommunikasjon fra NordPass.
                </p>
              </ColorBlock>
            </section>

            <Divider color="#F97316" />

            {/* ── Section 2: Meldingstyper ── */}
            <section id="meldingstyper" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="02"
                tag="§ 2 — TYPER SMS-MELDINGER"
                title="Typer SMS-meldinger"
                color="#3B6FD4"
              />

              <BodyText>
                NordPass kan sende følgende typer SMS-meldinger til deg etter at du har gitt samtykke:
              </BodyText>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                {[
                  {
                    title: 'Tjenestevarsler',
                    desc: 'Viktige oppdateringer om ditt abonnement, betalingsbekreftelser, fornyelsespåminnelser og kontoendringer.',
                    color: '#3B6FD4',
                    icon: '🔔'
                  },
                  {
                    title: 'Veiinformasjon',
                    desc: 'Sanntidsvarsler om stengte veier, veiarbeid, ulykkesmeldinger og endringer i bompriser på dine lagrede ruter.',
                    color: '#F97316',
                    icon: '🛣️'
                  },
                  {
                    title: 'Markedsføring',
                    desc: 'Tilbud, kampanjer, nye funksjoner og nyheter fra NordPass — kun dersom du har samtykket til markedsføring via SMS.',
                    color: '#10B981',
                    icon: '📢'
                  },
                  {
                    title: 'Sikkerhetsvarsler',
                    desc: 'Tofaktorautentisering (2FA), innloggingsvarsler og sikkerhetskoder for å beskytte kontoen din.',
                    color: '#8B5CF6',
                    icon: '🔐'
                  },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      background: `${item?.color}08`,
                      border: `1px solid ${item?.color}20`,
                      borderTop: `3px solid ${item?.color}`,
                      padding: '20px'
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{item?.icon}</div>
                    <div
                      style={{
                        fontFamily: 'Syne, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: item?.color,
                        marginBottom: '8px'
                      }}
                    >
                      {item?.title}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.8125rem',
                        color: 'rgba(238,240,242,0.65)',
                        lineHeight: 1.6
                      }}
                    >
                      {item?.desc}
                    </div>
                  </div>
                ))}
              </div>

              <BodyText>
                NordPass vil aldri sende SMS-meldinger som inneholder sensitiv finansiell informasjon, passord eller personopplysninger utover det som er nødvendig for den aktuelle tjenesten.
              </BodyText>
            </section>

            <Divider color="#3B6FD4" />

            {/* ── Section 3: Frekvens ── */}
            <section id="frekvens" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="03"
                tag="§ 3 — MELDINGSFREKVENS"
                title="Meldingsfrekvens"
                color="#F97316"
              />

              <BodyText>
                Antall SMS-meldinger du mottar varierer avhengig av hvilke tjenester du bruker og hvilke varsler du har aktivert. Som en generell retningslinje:
              </BodyText>

              <div style={{ marginBottom: '28px' }}>
                {[
                  { type: 'Tjenestevarsler', freq: 'Etter behov (typisk 1–4 per måned)', color: '#3B6FD4' },
                  { type: 'Veiinformasjon', freq: 'Etter behov basert på dine lagrede ruter', color: '#F97316' },
                  { type: 'Markedsføring', freq: 'Maks 4 meldinger per måned', color: '#10B981' },
                  { type: 'Sikkerhetsvarsler', freq: 'Kun ved innlogging eller kontoendringer', color: '#8B5CF6' },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '14px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.04)'
                    }}
                  >
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        minWidth: '10px',
                        background: item?.color,
                        transform: 'rotate(45deg)'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontFamily: 'Syne, sans-serif',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          color: '#EEF0F2'
                        }}
                      >
                        {item?.type}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.75rem',
                        color: 'rgba(138,149,163,0.7)',
                        textAlign: 'right'
                      }}
                    >
                      {item?.freq}
                    </div>
                  </div>
                ))}
              </div>

              <PolaroidCallout title="Merk — Varierende frekvens" accent="#3B6FD4">
                Meldingsfrekvensen kan variere. I perioder med viktige veiendringer, systemoppdateringer eller kampanjer kan du motta flere meldinger enn normalt. Vi tilstreber alltid å begrense antall meldinger til det som er nødvendig og relevant.
              </PolaroidCallout>
            </section>

            <Divider color="#F97316" />

            {/* ── Section 4: Avmelding ── */}
            <section id="avmelding" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="04"
                tag="§ 4 — AVMELDING OG HJELP"
                title="Slik melder du deg av — og får hjelp"
                color="#10B981"
              />

              <BodyText>
                Du kan når som helst trekke tilbake samtykket ditt og avslutte SMS-abonnementet. NordPass respekterer din rett til å kontrollere kommunikasjonen du mottar.
              </BodyText>

              {/* STOPP + HJELP side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                <div
                  style={{
                    background: 'rgba(16,185,129,0.06)',
                    border: '2px solid rgba(16,185,129,0.3)',
                    padding: '32px',
                    textAlign: 'center'
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Impact, sans-serif',
                      fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                      color: '#10B981',
                      letterSpacing: '0.1em',
                      marginBottom: '12px'
                    }}
                  >
                    SVAR STOPP
                  </div>
                  <div
                    style={{
                      fontFamily: 'Georgia, serif',
                      fontSize: '0.9375rem',
                      color: 'rgba(238,240,242,0.65)',
                      lineHeight: 1.65
                    }}
                  >
                    Send <strong style={{ color: '#10B981' }}>STOPP</strong> som svar på en hvilken som helst SMS fra NordPass for å avmelde deg umiddelbart fra alle SMS-meldinger.
                  </div>
                </div>

                <div
                  style={{
                    background: 'rgba(59,111,212,0.06)',
                    border: '2px solid rgba(59,111,212,0.3)',
                    padding: '32px',
                    textAlign: 'center'
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Impact, sans-serif',
                      fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                      color: '#3B6FD4',
                      letterSpacing: '0.1em',
                      marginBottom: '12px'
                    }}
                  >
                    SVAR HJELP
                  </div>
                  <div
                    style={{
                      fontFamily: 'Georgia, serif',
                      fontSize: '0.9375rem',
                      color: 'rgba(238,240,242,0.65)',
                      lineHeight: 1.65
                    }}
                  >
                    Send <strong style={{ color: '#3B6FD4' }}>HJELP</strong> som svar på en SMS fra NordPass for å motta kundestøtteinformasjon og kontaktdetaljer direkte på SMS.
                  </div>
                </div>
              </div>

              <BodyText>
                Du kan også melde deg av via følgende metoder:
              </BodyText>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
                {[
                  { method: 'SMS', desc: 'Svar STOPP på en melding fra NordPass', color: '#10B981' },
                  { method: 'Konto', desc: 'Logg inn på nordpassno.com → Kontoinnstillinger → Varslingsinnstillinger', color: '#3B6FD4' },
                  { method: 'E-post', desc: 'Send en e-post til hei@nordpassno.com med emne «Avmeld SMS»', color: '#F97316' },
                  { method: 'Telefon', desc: 'Ring +47 20 94 89 03 i åpningstiden Man–Fre 08:00–20:00', color: '#8B5CF6' },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      padding: '16px 20px',
                      background: `${item?.color}06`,
                      border: `1px solid ${item?.color}18`
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        color: item?.color,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        minWidth: '60px',
                        paddingTop: '2px'
                      }}
                    >
                      {item?.method}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        color: 'rgba(238,240,242,0.7)',
                        lineHeight: 1.55
                      }}
                    >
                      {item?.desc}
                    </div>
                  </div>
                ))}
              </div>

              <BodyText>
                Avmelding trer i kraft innen 24 timer. Etter avmelding vil du ikke lenger motta markedsføring via SMS. Merk at du fortsatt kan motta transaksjonelle meldinger (f.eks. betalingsbekreftelser) som er nødvendige for å levere tjenesten du har bestilt.
              </BodyText>
            </section>

            <Divider color="#10B981" />

            {/* ── Section 5: Kostnader ── */}
            <section id="kostnader" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="05"
                tag="§ 5 — PRISER OG OPERATØRANSVAR"
                title="Priser og operatøransvar"
                color="#F97316"
              />

              <BodyText>
                NordPass tar ikke betalt for SMS-tjenesten i seg selv. Det er imidlertid viktig å være klar over at din mobiloperatør kan belaste deg for mottatte og sendte meldinger i henhold til din mobilabonnementsavtale.
              </BodyText>

              {/* Prominent rates + carrier disclaimer */}
              <div
                style={{
                  background: 'rgba(249,115,22,0.06)',
                  border: '2px solid rgba(249,115,22,0.25)',
                  borderLeft: '4px solid #F97316',
                  padding: '28px 32px',
                  marginBottom: '28px'
                }}
              >
                <div
                  style={{
                    fontFamily: 'Syne, sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#F97316',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginBottom: '14px'
                  }}
                >
                  Meldingspriser og operatøransvar
                </div>
                <p
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: '1rem',
                    color: 'rgba(238,240,242,0.8)',
                    lineHeight: 1.8,
                    margin: '0 0 14px 0'
                  }}
                >
                  <strong style={{ color: '#F97316' }}>Meldingspriser og datapriser fra din mobiloperatør kan påløpe.</strong>{' '}
                  Sjekk vilkårene for ditt mobilabonnement hos din operatør for informasjon om eventuelle kostnader knyttet til mottak og sending av SMS-meldinger.
                </p>
                <p
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: '1rem',
                    color: 'rgba(238,240,242,0.8)',
                    lineHeight: 1.8,
                    margin: 0
                  }}
                >
                  <strong style={{ color: '#EEF0F2' }}>Mobiloperatørene er ikke ansvarlige</strong>{' '}
                  for forsinkede eller ikke-leverte SMS-meldinger. Leveringsproblemer kan skyldes nettverksforstyrrelser, feil telefonnummer eller andre tekniske forhold utenfor NordPass sin kontroll.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                {[
                  { label: 'NordPass SMS-tjeneste', value: 'Gratis', note: 'Ingen kostnad fra NordPass', color: '#10B981' },
                  { label: 'Mobiloperatørkostnader', value: 'Varierer', note: 'Avhenger av ditt abonnement', color: '#F97316' },
                  { label: 'Internasjonale meldinger', value: 'Kan påløpe', note: 'Sjekk med din operatør', color: '#3B6FD4' },
                  { label: 'Operatøransvar', value: 'Ikke ansvarlig', note: 'For forsinkede/ikke-leverte meldinger', color: '#8B5CF6' },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      background: `${item?.color}08`,
                      border: `1px solid ${item?.color}20`,
                      padding: '20px',
                      textAlign: 'center'
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.625rem',
                        color: 'rgba(138,149,163,0.5)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        marginBottom: '8px'
                      }}
                    >
                      {item?.label}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Impact, sans-serif',
                        fontSize: '1.25rem',
                        color: item?.color,
                        letterSpacing: '0.05em',
                        marginBottom: '6px'
                      }}
                    >
                      {item?.value}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: 'rgba(138,149,163,0.6)'
                      }}
                    >
                      {item?.note}
                    </div>
                  </div>
                ))}
              </div>

              <BodyText>
                NordPass er ikke ansvarlig for kostnader som påløper fra din mobiloperatør i forbindelse med mottak av SMS-meldinger fra oss. Vi anbefaler at du kontakter din operatør dersom du er usikker på hvilke kostnader som gjelder for ditt abonnement.
              </BodyText>
            </section>

            <Divider color="#F97316" />

            {/* ── Section 6: Personvern ── */}
            <section id="personvern" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="06"
                tag="§ 6 — PERSONVERN OG TELEFONNUMMER"
                title="Personvern og telefonnummer"
                color="#3B6FD4"
              />

              <BodyText>
                NordPass behandler ditt mobilnummer i samsvar med vår{' '}
                <Link
                  to="/privacy-policy-personvern"
                  style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}
                >
                  Personvernerklæring
                </Link>{' '}
                og gjeldende norsk personvernlovgivning, inkludert GDPR (EU 2016/679) og ekomloven.
              </BodyText>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
                {[
                  {
                    title: 'Innsamling',
                    desc: 'Ditt mobilnummer samles inn utelukkende for å levere SMS-tjenesten du har samtykket til.',
                    color: '#3B6FD4'
                  },
                  {
                    title: 'Lagring',
                    desc: 'Mobilnummeret lagres sikkert i NordPass sine systemer og slettes automatisk innen 30 dager etter avmelding.',
                    color: '#F97316'
                  },
                  {
                    title: 'Deling',
                    desc: 'Vi deler aldri ditt mobilnummer med tredjeparter for markedsføringsformål uten ditt eksplisitte samtykke.',
                    color: '#10B981'
                  },
                  {
                    title: 'Underleverandører',
                    desc: 'NordPass benytter godkjente SMS-leverandører som behandler data i henhold til GDPR og er bundet av databehandleravtaler.',
                    color: '#8B5CF6'
                  },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '20px',
                      padding: '20px',
                      background: `${item?.color}06`,
                      border: `1px solid ${item?.color}18`,
                      borderLeft: `4px solid ${item?.color}`
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: 'Syne, sans-serif',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          color: item?.color,
                          marginBottom: '6px'
                        }}
                      >
                        {item?.title}
                      </div>
                      <div
                        style={{
                          fontFamily: 'Georgia, serif',
                          fontSize: '0.9375rem',
                          color: 'rgba(238,240,242,0.7)',
                          lineHeight: 1.65
                        }}
                      >
                        {item?.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <PolaroidCallout title="Dine rettigheter — GDPR" accent="#3B6FD4">
                I henhold til GDPR har du rett til innsyn i, retting av og sletting av dine personopplysninger, inkludert ditt mobilnummer. Du kan utøve disse rettighetene ved å kontakte oss på hei@nordpassno.com eller via{' '}
                <Link
                  to="/privacy-policy-personvern"
                  style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}
                >
                  vår personvernerklæring
                </Link>.
              </PolaroidCallout>
            </section>

            <Divider color="#3B6FD4" />

            {/* ── Section 7: Kontakt ── */}
            <section id="kontakt" style={{ marginBottom: '80px' }}>
              <SectionHeader
                num="07"
                tag="§ 7 — KONTAKTINFORMASJON"
                title="Kontaktinformasjon"
                color="#F97316"
              />

              <BodyText>
                Dersom du har spørsmål om disse SMS-vilkårene, ønsker å melde deg av, eller vil utøve dine personvernrettigheter, kan du kontakte NordPass AS via følgende kanaler:
              </BodyText>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                {[
                  { label: 'E-post', value: 'hei@nordpassno.com', sub: 'Svar innen 1–2 virkedager', color: '#3B6FD4' },
                  { label: 'Telefon', value: '+47 20 94 89 03', sub: 'Man–Fre 08:00–20:00', color: '#F97316' },
                  { label: 'Post', value: 'Karl Johans gate 1', sub: '0154 Oslo, Norge', color: '#10B981' },
                ]?.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      background: `${item?.color}08`,
                      border: `1px solid ${item?.color}20`,
                      padding: '24px',
                      textAlign: 'center'
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.625rem',
                        color: item?.color,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        marginBottom: '10px'
                      }}
                    >
                      {item?.label}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Syne, sans-serif',
                        fontSize: '0.9375rem',
                        fontWeight: 700,
                        color: '#EEF0F2',
                        marginBottom: '4px'
                      }}
                    >
                      {item?.value}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: 'rgba(138,149,163,0.6)'
                      }}
                    >
                      {item?.sub}
                    </div>
                  </div>
                ))}
              </div>

              <ColorBlock color="#F97316">
                <p
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: '0.9375rem',
                    color: 'rgba(238,240,242,0.65)',
                    lineHeight: 1.75,
                    margin: 0
                  }}
                >
                  <strong style={{ color: '#F97316', fontStyle: 'normal' }}>Klager:</strong>{' '}
                  Dersom du mener at NordPass ikke overholder disse vilkårene eller gjeldende personvernlovgivning, har du rett til å klage til{' '}
                  <a
                    href="https://www.datatilsynet.no"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#F97316', textDecoration: 'underline', textDecorationColor: 'rgba(249,115,22,0.4)' }}
                  >
                    Datatilsynet (datatilsynet.no)
                  </a>.
                </p>
              </ColorBlock>

              {/* Related pages */}
              <div
                style={{
                  marginTop: '48px',
                  padding: '32px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                <div
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '0.625rem',
                    color: 'rgba(138,149,163,0.5)',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    marginBottom: '20px'
                  }}
                >
                  Relaterte juridiske dokumenter
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Personvernerklæring', path: '/privacy-policy-personvern', color: '#3B6FD4' },
                    { label: 'Vilkår & Betingelser', path: '/terms-conditions-vilk-r-betingelser', color: '#F97316' },
                    { label: 'Kontakt oss', path: '/nord-pass-contact-page', color: '#10B981' },
                  ]?.map((item, i) => (
                    <Link
                      key={i}
                      to={item?.path}
                      style={{
                        fontFamily: 'Syne, sans-serif',
                        fontSize: '0.8125rem',
                        fontWeight: 700,
                        color: item?.color,
                        textDecoration: 'none',
                        padding: '10px 20px',
                        background: `${item?.color}08`,
                        border: `1px solid ${item?.color}25`,
                        transition: 'all 150ms ease'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${item?.color}15`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${item?.color}08`; }}
                    >
                      {item?.label} →
                    </Link>
                  ))}
                </div>
              </div>
            </section>

          </main>
        </div>

        {/* Footer strip */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: '#030710',
            padding: '32px 5%'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}
          >
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.6875rem',
                color: 'rgba(138,149,163,0.4)',
                letterSpacing: '0.06em'
              }}
            >
              © 2026 NordPass AS — SMS Opt-in Vilkår v1.1 — Sist oppdatert Mars 2026
            </div>
            <div style={{ display: 'flex', gap: '24px' }}>
              {[
                { label: 'Hjem', path: '/nord-pass-landing-page' },
                { label: 'Personvern', path: '/privacy-policy-personvern' },
                { label: 'Vilkår', path: '/terms-conditions-vilk-r-betingelser' },
                { label: 'Kontakt', path: '/nord-pass-contact-page' },
              ]?.map((item, i) => (
                <Link
                  key={i}
                  to={item?.path}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: 'rgba(138,149,163,0.4)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#8A95A3'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(138,149,163,0.4)'; }}
                >
                  {item?.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .sms-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
};

export default SMSOptinVilkarPage;
